/** Org settings reads — rec_org_settings via MIGRATION_CRM2_PHASE6_READS.sql
 * (LIVE since 2026-07-16). Row shape: nat_key = settings key ('NR_MASTER',
 * 'JOB_TAGS', …), value = jsonb. Read-only; editing defaults stays in v1. */
import { http } from "../../lib/http";
import { idbGet, idbSet } from "../../lib/idb";
import { SUPABASE_URL, SUPABASE_ANON_KEY } from "../../config";
import type { RecOp } from "../../lib/outbox";
import { applyIntent, isEmptyIntent } from "./ledgerIntent";

const CACHE = "org_settings_v1";

/** Org-settings keys the v2 write door (crm2_apply_ops, section 'settings')
 * whitelists for editing. MUST stay in sync with MIGRATION_CRM2_PHASE17. AGENT
 * is the Text Agent blob (Phase 13); AUTOMATION + NR_MASTER are the two editable
 * config tabs in the Settings page (Phase 17). Everything else in
 * rec_org_settings is read-only from v2 (backend/integration/runtime state). */
export const WRITABLE_SETTING_KEYS = ["AGENT", "AUTOMATION", "NR_MASTER", "PERMS", "DIALPAD", "NR_BY"] as const;
export type WritableSettingKey = (typeof WRITABLE_SETTING_KEYS)[number];

/** Generic settings put op. Value is the WHOLE new value for the key (v1 and the
 * core replace the row wholesale — same as opPutAgent). Object-valued keys only
 * for the Phase-17 whitelist. */
export function opPutSetting(key: WritableSettingKey, value: Record<string, unknown>): RecOp {
  return { s: "settings", op: "put", k: key, d: value };
}

/** ShowingTime feed url (Phase 27) — the ONE string-valued settings key the
 * door accepts. Whole-value replace, like every settings put. Requires
 * MIGRATION_CRM2_PHASE27_SHOWINGTIME.sql — until it's applied the door
 * rejects with "settings key SHOWINGTIME_FEED not writable from v2". */
export function opPutFeedUrl(url: string): RecOp {
  return { s: "settings", op: "put", k: "SHOWINGTIME_FEED", d: url.trim() };
}

/** v1 Automation settings — the three org toggles (AUTOMATION key). Missing =
 * ON (v1 treats absence as enabled; e.g. stageTasks default-on in app.tsx). */
export interface Automation {
  autoTag: boolean;
  priceDrop: boolean;
  stageTasks: boolean;
}

export function narrowAutomation(raw: unknown): Automation {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const on = (v: unknown) => v !== false; // absence or anything but explicit false = ON (v1)
  return { autoTag: on(o["autoTag"]), priceDrop: on(o["priceDrop"]), stageTasks: on(o["stageTasks"]) };
}

export const AUTOMATION_LABELS: { key: keyof Automation; label: string; help: string }[] = [
  { key: "autoTag", label: "Auto-tag new leads", help: "Let the system apply job tags to incoming leads automatically." },
  { key: "priceDrop", label: "Price-drop alerts", help: "Flag leads when their price drops while they're in the CRM." },
  { key: "stageTasks", label: "Auto stage checklists", help: "Create the standard task checklist when a deal enters a new stage." },
];

/** ROI Defaults (NR_MASTER) — the fields the calculator + est-money read. Labels
 * mirror v1's master editor; values are numbers (percent or dollars per unit). */
export const ROI_DEFAULT_FIELDS: { key: string; label: string; unit: "%" | "$" | "mo" | "$/mo" | "×" | "yr"; group: string }[] = [
  { key: "ltv", label: "Loan-to-value", unit: "%", group: "Financing" },
  { key: "rate", label: "Interest rate", unit: "%", group: "Financing" },
  { key: "pts", label: "Origination points", unit: "%", group: "Financing" },
  { key: "svc", label: "Loan service fee", unit: "$", group: "Financing" },
  { key: "hold", label: "Hold period", unit: "mo", group: "Timeline" },
  { key: "utilm", label: "Utility months", unit: "mo", group: "Timeline" },
  { key: "utilmo", label: "Utilities", unit: "$/mo", group: "Carrying costs" },
  { key: "insm", label: "Insurance months", unit: "mo", group: "Carrying costs" },
  { key: "insmo", label: "Insurance", unit: "$/mo", group: "Carrying costs" },
  { key: "staging", label: "Staging", unit: "$", group: "Carrying costs" },
  { key: "buy", label: "Buying costs", unit: "%", group: "Transaction" },
  { key: "sell", label: "Selling costs", unit: "%", group: "Transaction" },
  { key: "kiwi", label: "Misc. reserve", unit: "%", group: "Transaction" },
  // BRRRR defaults (v1 NR_MASTER br_*) — these drive the BRRRR side of the Net
  // ROI calc AND the Investor Buy-Box matcher's CoC / cap / cash-flow scoring.
  { key: "br_rent", label: "Market rent", unit: "$/mo", group: "BRRRR — Rent & refinance" },
  { key: "br_refiltv", label: "Refinance LTV", unit: "%", group: "BRRRR — Rent & refinance" },
  { key: "br_refirate", label: "Refinance rate", unit: "%", group: "BRRRR — Rent & refinance" },
  { key: "br_refiterm", label: "Refinance term", unit: "yr", group: "BRRRR — Rent & refinance" },
  { key: "br_vac", label: "Vacancy", unit: "%", group: "BRRRR — Operating" },
  { key: "br_mgmt", label: "Management", unit: "%", group: "BRRRR — Operating" },
  { key: "br_maint", label: "Maintenance", unit: "%", group: "BRRRR — Operating" },
  { key: "br_capex", label: "CapEx reserve", unit: "%", group: "BRRRR — Operating" },
  { key: "br_taxes", label: "Annual property taxes", unit: "$", group: "BRRRR — Operating" },
  { key: "br_ins", label: "Annual insurance", unit: "$", group: "BRRRR — Operating" },
  { key: "br_hoa", label: "Monthly HOA", unit: "$", group: "BRRRR — Operating" },
];

export function narrowRoiMaster(raw: unknown): Record<string, number> {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const out: Record<string, number> = {};
  for (const k of Object.keys(o)) {
    const v = o[k];
    // real numbers, or non-empty numeric strings only — never coerce null/bool
    // to 0 (that would silently zero out a default the user never touched).
    if (typeof v === "number") {
      if (Number.isFinite(v)) out[k] = v;
    } else if (typeof v === "string" && v.trim() !== "") {
      const n = Number(v);
      if (Number.isFinite(n)) out[k] = n;
    }
  }
  return out;
}

/* --------------------------------------------------------- Partner Access -- */
/** A payment a partner made you (v1 partnerLogPayment row, minus the per-deal
 * tag — v2 keeps it flat: amount + free-text note + a display date string). */
export interface PartnerPayment {
  amount: number;
  note: string;
  date: string;
  /** the shared deal (natKey) this payment is for — "" = general/unassigned */
  deal?: string;
}

/** A partner grant (v1 window.PARTNERS value). `token` is the secret in the
 * private read-only link; `deals` holds the shared deal keys; `payments` is the
 * ledger of money they've paid you. Stored as an ARRAY under the PARTNERS key. */
export interface Partner {
  id: string;
  name: string;
  token: string;
  deals: string[];
  payments: PartnerPayment[];
}

/** Defensive read of the PARTNERS blob (an array of grants). Anything malformed
 * — non-array, rows missing an id/token, junk fields — is dropped rather than
 * crashing the Settings page, same posture as narrowRoiMaster. */
export function narrowPartners(raw: unknown): Partner[] {
  if (!Array.isArray(raw)) return [];
  const out: Partner[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object" || Array.isArray(r)) continue;
    const o = r as Record<string, unknown>;
    const id = typeof o["id"] === "string" ? o["id"] : "";
    const token = typeof o["token"] === "string" ? o["token"] : "";
    if (!id || !token) continue; // no identity or no link = an unusable grant
    const dealsRaw = o["deals"];
    const deals = Array.isArray(dealsRaw) ? dealsRaw.filter((x): x is string => typeof x === "string") : [];
    out.push({
      id,
      token,
      name: typeof o["name"] === "string" ? o["name"] : "",
      deals,
      payments: narrowPartnerPayments(o["payments"]),
    });
  }
  return out;
}

function narrowPartnerPayments(raw: unknown): PartnerPayment[] {
  if (!Array.isArray(raw)) return [];
  const out: PartnerPayment[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object" || Array.isArray(r)) continue;
    const o = r as Record<string, unknown>;
    const a = o["amount"];
    // real number or non-empty numeric string only — never coerce null/bool → 0
    const amount = typeof a === "number" ? a : typeof a === "string" && a.trim() !== "" ? Number(a) : NaN;
    if (!Number.isFinite(amount)) continue;
    out.push({
      amount,
      note: typeof o["note"] === "string" ? o["note"] : "",
      date: typeof o["date"] === "string" ? o["date"] : "",
      ...(typeof o["deal"] === "string" && o["deal"] ? { deal: o["deal"] } : {}),
    });
  }
  return out;
}

/** Settings-door put of the WHOLE partners array (v1/core replace the row
 * wholesale — same shape as opPutContractors). PARTNERS joined the door's
 * settings whitelist in MIGRATION_CRM2_PHASE26 (it was missing before — every
 * partner save died at the door until then). */
export function opPutPartners(all: Partner[]): RecOp {
  return withLedgerBase({ s: "settings", op: "put", k: "PARTNERS", d: all });
}

/** SOW templates ledger (Phase 28) — the whole org SCOPE_TPLS array in v1's
 * exact shape. Same stale-write guard as every settings ledger. Requires
 * MIGRATION_CRM2_PHASE28_SCOPE_TPLS.sql; until it's pasted the door rejects
 * with "settings key SCOPE_TPLS not writable from v2". */
export function opPutScopeTpls(all: unknown[]): RecOp {
  return withLedgerBase({ s: "settings", op: "put", k: "SCOPE_TPLS", d: all });
}

// ---- Phase 26: settings-ledger stale-write guard (client half) -------------
// The door accepts an optional `base` = md5 etag of the ledger value this
// write replaces (served by crm2_settings_etags()). Only the FIRST write
// after a load carries it: the etag is consumed at op build, so same-tab
// rapid edits are never falsely rejected, while a stale tab's first write
// (the dangerous one) is caught server-side with crm2_stale_write.

/** The whole-array settings blobs the guard covers (door PHASE26 list +
 * SCOPE_TPLS since PHASE28). */
export const SETTINGS_LEDGER_KEYS = [
  "TXN_CONTRACTORS", "TXNEXP", "EXP2", "COMPANY_EXP",
  "INVESTORS", "BUYERS", "WHOLESALER_QUEUE", "PARTNERS", "SCOPE_TPLS",
] as const;

const etags = new Map<string, string>();

/** Load the etag map (fires with every settings fetch; fail-soft — an old DB
 * without the RPC just means the guard never arms). */
export function setSettingsEtags(raw: unknown): void {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "string" && /^[0-9a-f]{32}$/.test(v)) etags.set(k, v);
  }
}

/** Consume-once: returns the etag for a ledger key and CLEARS it, so only the
 * first post-load write is guarded (see the note above). */
export function takeSettingsEtag(key: string): string | null {
  const v = etags.get(key) ?? null;
  etags.delete(key);
  return v;
}

/** Attach `base` to a settings-ledger put when a fresh etag is available. */
export function withLedgerBase(op: RecOp): RecOp {
  if (op.s !== "settings" || op.op !== "put") return op;
  if (!(SETTINGS_LEDGER_KEYS as readonly string[]).includes(op.k)) return op;
  const b = takeSettingsEtag(op.k);
  return b ? { ...op, base: b } : op;
}

/** Fetch the server-computed etag map (crm2_settings_etags, Phase 26).
 * Fail-soft by design: any error leaves the guard unarmed. */
export async function fetchSettingsEtags(token: string, signal?: AbortSignal): Promise<void> {
  try {
    const m = await http<unknown>(`${SUPABASE_URL}/rest/v1/rpc/crm2_settings_etags`, {
      method: "POST",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      body: {},
      attempts: 1,
      timeoutMs: 12000,
      signal,
    });
    setSettingsEtags(m);
  } catch {
    /* guard stays unarmed — writes behave exactly as before Phase 26 */
  }
}

/** Does this error mean the door rejected a whole-array settings write because
 * its base etag went stale (PHASE26 crm2_stale_write)? Checks the message AND
 * the HTTP body — PostgREST surfaces the exception text in either. */
export function isStaleWrite(e: unknown): boolean {
  if (e === null || typeof e !== "object") return false;
  const msg = (e as Error).message ?? "";
  const body = (e as { body?: unknown }).body;
  const bodyTxt = typeof body === "string" ? body : body !== null && typeof body === "object" ? JSON.stringify(body) : "";
  return msg.includes("crm2_stale_write") || bodyTxt.includes("crm2_stale_write");
}

/** Re-arm a batch after a crm2_stale_write reject: attach the FRESH etag to
 * every settings-ledger put in the batch (leaving every other op untouched).
 * Call fetchSettingsEtags first so the store holds the server's current tags.
 * Semantics: this tab retries ONCE with the world as it is NOW — the user's
 * optimistic UI is exactly what they asked for, so last-write-wins from here,
 * while the fresh base still guards against yet another concurrent writer.
 * Regression this exists for: delete an expense → add one seconds later; a
 * racing etag re-arm made the second write carry a stale base → the door
 * rejected it → the new expense silently died in the dead-letter queue. */
export function rebaseLedgerOps(ops: RecOp[]): RecOp[] {
  return ops.map((op) => {
    if (op.s !== "settings" || op.op !== "put" || !(SETTINGS_LEDGER_KEYS as readonly string[]).includes(op.k)) return op;
    const fresh = takeSettingsEtag(op.k);
    if (fresh) return { ...op, base: fresh };
    // no fresh tag (RPC unavailable) → strip the stale base so the retry isn't
    // doomed; behaves exactly like a pre-Phase-26 write.
    const { base: _stale, ...rest } = op;
    return rest as RecOp;
  });
}

/** Delivery-time MERGE for ledger puts that carry an `intent` (2026-09-02).
 *
 * Instead of trusting the array the tab built from its own (possibly
 * days-old) copy, re-read the server's current value for that key, re-apply
 * just the rows this tab added/removed, and send THAT with the fresh etag.
 * Rows other devices added since this tab loaded survive. Ops without an
 * intent (or on non-ledger keys) pass through untouched.
 *
 * Returns the wire ops plus, per merged key, the array the server now holds
 * — the caller folds it back into local state so this tab also SEES what the
 * other devices did. Network failure throws (the outbox keeps the op and
 * retries later, exactly like a failed send). */
export async function mergeLedgerOps(
  token: string,
  ops: RecOp[],
): Promise<{ ops: RecOp[]; merged: Record<string, unknown[]> }> {
  const merged: Record<string, unknown[]> = {};
  const want = ops.filter(
    (o) => o.s === "settings" && o.op === "put" && !isEmptyIntent(o.intent)
      && (SETTINGS_LEDGER_KEYS as readonly string[]).includes(o.k),
  );
  if (want.length === 0) return { ops, merged };
  await fetchSettingsEtags(token); // fresh tags for the keys we're about to rebuild
  const out: RecOp[] = [];
  for (const o of ops) {
    if (!want.includes(o) || !o.intent) { out.push(o); continue; }
    const rows = await http<{ value?: unknown }[]>(
      `${SUPABASE_URL}/rest/v1/rec_org_settings?select=value&nat_key=eq.${encodeURIComponent(o.k)}&limit=1`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, attempts: 1, timeoutMs: 15000 },
    );
    const cur = Array.isArray(rows) && rows[0] && Array.isArray(rows[0].value) ? (rows[0].value as unknown[]) : [];
    const next = applyIntent(cur, o.intent);
    merged[o.k] = next;
    const fresh = takeSettingsEtag(o.k);
    const { base: _b, ...rest } = o;
    out.push(fresh ? { ...rest, d: next, base: fresh } : { ...rest, d: next });
  }
  return { ops: out, merged };
}

export const SETTINGS_MSG = {
  editPhaseB: "Editing unlocks in the go-live session",
  savedLocal: "Saved — will sync to the shared settings",
  saveFail: "Couldn't save that setting — nothing changed",
  integrationV1: "Connect this in v1 for now — it needs your sign-in, which stays with you.",
  destructiveV1: "These stay in v1 on purpose — they permanently erase data.",
};

export async function cachedOrgSettings(): Promise<Record<string, unknown> | null> {
  const c = await idbGet<{ at: number; map: Record<string, unknown> }>(CACHE);
  return c && c.map && typeof c.map === "object" ? c.map : null;
}

export async function fetchOrgSettings(token: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
  // refresh the stale-write-guard etags alongside every settings load
  // (fire-and-forget: reads must never wait on the guard)
  void fetchSettingsEtags(token, signal);
  const rows = await http<{ nat_key?: unknown; value?: unknown }[]>(
    `${SUPABASE_URL}/rest/v1/rec_org_settings?select=nat_key,value&limit=200`,
    { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` }, signal, timeoutMs: 15000 },
  );
  const map: Record<string, unknown> = {};
  for (const r of Array.isArray(rows) ? rows : []) {
    if (typeof r.nat_key === "string" && r.nat_key) map[r.nat_key] = r.value;
  }
  await idbSet(CACHE, { at: Date.now(), map });
  return map;
}
